import { z } from 'zod';
import { normalizePhoneNumber } from './normalizePhoneNumber';

const MAX_LENGTH = 50;
const TOO_LONG = `Must be at most ${MAX_LENGTH} characters long`;
export const INVALID_PHONE_NUMBER = 'Invalid phone number';

/**
 * A telephone number, stored in strict E.164 (`+447911123456`) — the format the
 * Platform and Awell require. Whatever format a member or partner sends is
 * repaired into it where that can be done with certainty (a missing `+`, a
 * missing country code read as UK, a trunk 0 after the country code; see
 * {@link normalizePhoneNumber}); only a number that has no valid reading is
 * rejected, with "Invalid phone number".
 *
 * An empty value is kept as it is, for an optional number a form sends blank.
 *
 * @param requiredMessage reported when the value is missing, empty or only
 * spaces; omit for an optional number (add `.optional()` at the field)
 */
export const phoneValidation = (requiredMessage?: string) => {
  const phone = z.string(requiredMessage).trim().max(MAX_LENGTH, TOO_LONG);

  return (
    (requiredMessage ? phone.min(1, requiredMessage) : phone)
      .transform((value, ctx) => {
        if (value === '') {
          return value;
        }
        const e164 = normalizePhoneNumber(value);
        if (e164 === undefined) {
          ctx.addIssue({ code: 'custom', message: INVALID_PHONE_NUMBER });
          return z.NEVER;
        }
        return e164;
      })
      // the transform is unrepresentable for `z.toJSONSchema`, which would
      // document the field as "anything"; the metadata restores its shape
      .meta({
        type: 'string',
        maxLength: MAX_LENGTH,
        description:
          'Telephone number. Stored in E.164 (e.g. "+447911123456"); a number ' +
          'without a country code is read as a UK one, and spaces, a missing "+" ' +
          'or a 0 after the country code are corrected.',
        examples: ['+447911123456', '07911 123456'],
      })
  );
};
